/**
 * Pilares y contacto del landing: qué se muestra y qué se guarda.
 *
 * Los dos los escribe el seminario en Configuración → Landing. Nada viene de
 * fábrica: los pilares de antes eran un texto igual para todos los seminarios,
 * y por eso se quitaron.
 */
import type { LandingContact, LandingPillar, LandingPillarIcon } from '@dosfilos/domain';
import { safeExternalUrl } from './featured-events';

export const MAX_PILLARS = 4;
export const MAX_PILLAR_TEXT = 220;
export const PILLAR_ICONS: LandingPillarIcon[] = [
  'book',
  'people',
  'school',
  'church',
  'heart',
  'globe',
];

export type PillarError = 'title' | 'text';

export function validatePillar(p: LandingPillar): PillarError[] {
  const errors: PillarError[] = [];
  if (!p.title?.trim()) errors.push('title');
  if (!p.text?.trim()) errors.push('text');
  return errors;
}

export function visiblePillars(list: readonly LandingPillar[] | null | undefined): LandingPillar[] {
  return (list ?? []).filter((p) => validatePillar(p).length === 0).slice(0, MAX_PILLARS);
}

export function normalizePillars(list: readonly LandingPillar[]): LandingPillar[] {
  return list.slice(0, MAX_PILLARS).map((p) => ({
    id: p.id,
    title: p.title.trim(),
    text: p.text.trim().slice(0, MAX_PILLAR_TEXT),
    icon: PILLAR_ICONS.includes(p.icon) ? p.icon : 'book',
  }));
}

export function newPillar(id: string): LandingPillar {
  return { id, title: '', text: '', icon: 'book' };
}

// ─── Contacto ───────────────────────────────────────────────────────────────

const URL_FIELDS = ['mapsUrl', 'instagram', 'facebook', 'youtube', 'website'] as const;
const TEXT_FIELDS = ['address', 'city', 'phone'] as const;
export type ContactError = (typeof URL_FIELDS)[number] | 'whatsapp';

/** «+56 9 1234 5678» → «56912345678»; vacío o fuera de largo → null. */
export function whatsappDigits(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

export function whatsappHref(digits: string): string {
  return `https://wa.me/${digits}`;
}

/** Errores para el editor: un enlace que no es https o un WhatsApp sin número válido. */
export function contactErrors(raw: LandingContact | null | undefined): ContactError[] {
  const c = raw ?? {};
  const errors: ContactError[] = [];
  for (const f of URL_FIELDS) {
    const v = c[f]?.trim();
    if (v && !safeExternalUrl(v)) errors.push(f);
  }
  if (c.whatsapp?.trim() && !whatsappDigits(c.whatsapp)) errors.push('whatsapp');
  return errors;
}

/**
 * Lo que se guarda: textos recortados, enlaces solo https, WhatsApp solo con
 * dígitos y opcionales vacíos OMITIDOS (Firestore no acepta `undefined`). Sin
 * ningún dato devuelve `null`: no queda un objeto vacío guardado.
 */
export function normalizeContact(raw: LandingContact | null | undefined): LandingContact | null {
  const c = raw ?? {};
  const out: LandingContact = {};
  for (const f of TEXT_FIELDS) {
    const v = c[f]?.trim();
    if (v) out[f] = v;
  }
  for (const f of URL_FIELDS) {
    const v = safeExternalUrl(c[f]);
    if (v) out[f] = v;
  }
  const wa = whatsappDigits(c.whatsapp);
  if (wa) out.whatsapp = wa;
  return Object.keys(out).length > 0 ? out : null;
}

/** ¿Hay algo que mostrar en la columna de contacto, además del correo? */
export function hasContact(c: LandingContact | null | undefined): boolean {
  return normalizeContact(c) !== null;
}

/** Subir/bajar en una lista del editor (la misma regla que el cuerpo académico). */
export { moveFaculty as moveItem } from './landing-faculty';
